import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import { ChevronDown, ChevronUp, Filter, X } from 'lucide-react';
import type { InvoiceFilter, InvoiceStatus } from '../../types';
import { useUsers } from '../../hooks/useUsers';
import { usePermissions } from '../../hooks/usePermissions';

interface InvoiceFiltersProps {
  filters: InvoiceFilter;
  onFilterChange: (filters: InvoiceFilter) => void;
  onReset: () => void;
  projects: Array<{ projectId: string; name: string }>;
  companyId?: string;
}

const InvoiceFilters: React.FC<InvoiceFiltersProps> = ({
  filters,
  onFilterChange,
  onReset,
  projects,
  companyId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // Permissions
  const { canManageUsers } = usePermissions();
  
  // Fetch users for admin filter
  const { data: users = [] } = useUsers({ activeOnly: true });
  
  const hasActiveFilters = Object.keys(filters).some(key => {
    const value = filters[key as keyof InvoiceFilter];
    return value !== undefined && value !== '' && value !== '__all__';
  });

  const activeFilterCount = Object.keys(filters).filter(key => {
    const value = filters[key as keyof InvoiceFilter];
    return value !== undefined && value !== '' && value !== '__all__';
  }).length;

  const handleChange = (field: keyof InvoiceFilter, value: any) => {
    onFilterChange({
      ...filters,
      [field]: value === '__all__' ? undefined : value,
    });
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="mb-6">
      <div className="flex items-center justify-between gap-4">
        <CollapsibleTrigger asChild>
          <Button variant="outline" className="gap-2">
            <Filter className="h-4 w-4" />
            Filtros
            {hasActiveFilters && (
              <span className="ml-1 rounded-full bg-blue-600 px-2 py-0.5 text-xs text-white">
                {activeFilterCount}
              </span>
            )}
            {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </CollapsibleTrigger>
        
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={onReset} className="gap-2">
            <X className="h-4 w-4" />
            Limpiar filtros
          </Button>
        )}
      </div>

      <CollapsibleContent className="mt-4">
        <div className="rounded-lg border bg-card p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Búsqueda */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buscar
              </label>
              <Input
                type="text"
                value={filters.search || ''}
                onChange={(e) => handleChange('search', e.target.value)}
                placeholder="N° factura, cliente, RUT..."
              />
            </div>

            {/* Proyecto */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Proyecto
              </label>
              <Select
                value={filters.projectId || '__all__'}
                onValueChange={(value) => handleChange('projectId', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos los proyectos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos los proyectos</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.projectId} value={project.projectId}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Estado */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado
              </label>
              <Select
                value={filters.status || '__all__'}
                onValueChange={(value) => handleChange('status', value as InvoiceStatus)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos los estados" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos los estados</SelectItem>
                  <SelectItem value="pending">Pendiente</SelectItem>
                  <SelectItem value="paid">Pagada</SelectItem>
                  <SelectItem value="overdue">Vencida</SelectItem>
                  <SelectItem value="cancelled">Anulada</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Empresa Emisora - solo para SOLARMAQENERGY */}
            {companyId === '776954438' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Empresa Emisora
                </label>
                <Select
                  value={filters.issuerName || '__all__'}
                  onValueChange={(value) => handleChange('issuerName', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todas las empresas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todas las empresas</SelectItem>
                    <SelectItem value="SOLARMAQENERGY">SOLARMAQENERGY</SelectItem>
                    <SelectItem value="JCA">JCA</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Fecha desde */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Emisión desde
              </label>
              <Input
                type="date"
                value={filters.startDate || ''}
                onChange={(e) => handleChange('startDate', e.target.value)}
              />
            </div>

            {/* Fecha hasta */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Emisión hasta
              </label>
              <Input
                type="date"
                value={filters.endDate || ''}
                onChange={(e) => handleChange('endDate', e.target.value)}
              />
            </div>

            {/* Monto mínimo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monto mínimo
              </label>
              <Input
                type="number"
                value={filters.minAmount || ''}
                onChange={(e) => handleChange('minAmount', e.target.value ? Number(e.target.value) : undefined)}
                placeholder="0"
                min="0"
              />
            </div>

            {/* Monto máximo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monto máximo
              </label>
              <Input
                type="number"
                value={filters.maxAmount || ''}
                onChange={(e) => handleChange('maxAmount', e.target.value ? Number(e.target.value) : undefined)}
                placeholder="Sin límite"
                min="0"
              />
            </div>

            {/* Usuario (admin only) */}
            {canManageUsers && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Usuario
                </label>
                <Select
                  value={filters.userId || '__all__'}
                  onValueChange={(value) => handleChange('userId', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos los usuarios" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos los usuarios</SelectItem>
                    {users.map((user) => (
                      <SelectItem key={user.userId} value={user.userId}>
                        {user.firstName} {user.lastName} ({user.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default InvoiceFilters;
